import React, { useState } from "react";
import invoke from "../../../common/utils/invoke";
import "./Form.scss";
import IndiaFlag from "../../../assets/images/india-flag.png";
import Modal from "../../../common/components/Modal.jsx";
import BrandLoader from "../../../common/components/BrandLoader.jsx";

import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  UI_LABELS,
} from "../../../constants";

import {
  validateBySchema,
  validateFieldBySchema,
  advisorFormSchema,
} from "../../../common/utils/validation.js";

const initialFormState = {
  name: "",
  mobile: "",
  email: "",
  employeeCode: "",
  category: "",
  concerns: "",
  advisorName: "",
  advisorMobileNumber: "",
  advisorEmailID: "",
  advisorCode: "",
};

const AdvisorForm = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMsg, setApiErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    let sanitizedValue = value;
    if (name === "name" || name === "advisorName") {
      sanitizedValue = value
        .replace(/[^a-zA-Z\s]/g, "") // remove special chars & numbers
        .replace(/\s{2,}/g, " ") // prevent multiple spaces
        .trimStart(); // avoid leading space
    }

    if (name === "mobile" || name === "advisorMobileNumber") {
      sanitizedValue = value.replace(/\D/g, "").replace(/^0+/, "");
    }

    setFormData((prev) => {
      const updatedForm = {
        ...prev,
        [name]: sanitizedValue,
      };

      const fieldError = validateFieldBySchema(
        name,
        sanitizedValue,
        advisorFormSchema,
        updatedForm,
      );

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldError,
      }));

      return updatedForm;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateBySchema(formData, advisorFormSchema);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      await invoke.submitGroupForm({
        fullName: formData.name,
        mobileNumber: Number(`91${formData.mobile}`),
        emailID: formData.email,
        employeeCode: formData.employeeCode,
        category: formData.category,
        concerns: formData.concerns,
        advisorName: formData.advisorName,
        advisorMobileNumber:Number(`91${formData.advisorMobileNumber}`),
        advisorEmailID: formData.advisorEmailID,
        advisorCode: formData.advisorCode,
        policyCustomerType: "Advisor",
      });

      setShowSuccessModal(true);
      setFormData(initialFormState);
      setErrors({});
    } catch (err) {
      setApiErrorMsg(
        err?.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR,
      );
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    // run full schema validation
    const validationErrors = validateBySchema(formData, advisorFormSchema);

    // no errors + not submitting
    return Object.keys(validationErrors).length === 0 && !submitting;
  };
  return (
    <div className="form-wrapper">
      {submitting && (
        <div className="loader-overlay">
          <BrandLoader color="#97262A" loading={submitting} size={15} />
        </div>
      )}
      <div className="form">
        <div className="w-100 form-top-heading">
          <h6 className="regular-body bold">Policy Holder Details</h6>
        </div>
        <div className="form-row">
          <label className="label-one">Enter Full Name</label>
          <input
            id="name"
            name="name"
            type="text"
            className={`form-input ${errors.name ? "has-error" : ""}`}
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Avinash Sharma"
            autocomplete="off"
          />
          {errors.name && <div className="error-text">{errors.name}</div>}
        </div>
        <div className="form-row">
          <label className="label-one">Mobile No.</label>
          <div className="flag-input">
            <div className="flag-box">
              <img className="country-flag" src={IndiaFlag} alt="India" />
              <span className="dial-code">+91</span>
            </div>
            <input
              id="mobile"
              name="mobile"
              pattern="[0-9]*"
              inputMode="numeric"
              type="tel"
              maxLength={10}
              className={`form-input ${errors.mobile ? "has-error" : ""}`}
              value={formData.mobile}
              onChange={handleChange}
              placeholder="e.g. 98762 72383"
              autocomplete="off"
            />
          </div>
          {errors.mobile && (
            <div className="error-text">{errors.mobile}</div>
          )}
        </div>

        <div className="form-row">
          <label className="label-one">Email ID</label>
          <input
            id="email"
            name="email"
            type="email"
            className={`form-input ${errors.email ? "has-error" : ""}`}
            value={formData.email}
            onChange={handleChange}
            placeholder="Deepak@indusindnipponlife.com"
            autocomplete="off"
          />
          {errors.email && <div className="error-text">{errors.email}</div>}
        </div>

        <div className="form-row">
          <label className="label-one">Employee Code</label>
          <input
            id="employeeCode"
            name="employeeCode"
            type="text"
            className={`form-input ${errors.employeeCode ? "has-error" : ""}`}
            value={formData.employeeCode}
            onChange={handleChange}
            placeholder="Enter employee code"
            autocomplete="off"
          />
          {errors.employeeCode && (
            <div className="error-text">{errors.employeeCode}</div>
          )}
        </div>

        <div className="form-row">
          <label className="label-one">Category</label>
          <select
            id="category"
            value={formData.category}
            name="category"
            className={`form-input ${errors.category ? "has-error" : ""}`}
            onChange={handleChange}
          >
            <option value="">Select</option>
            <option value="claimsRelated">Claims Related</option>
            <option value="payoutRelated">Payout Related</option>
            <option value="salesRelated">Sales Related</option>
          </select>
          {errors.category && (
            <div className="error-text">{errors.category}</div>
          )}
        </div>

        <div className="seprater w-100"></div>

        <div className="w-100 form-top-heading">
          <h6 className="regular-body bold">Advisor Details</h6>
        </div>

        <div className="form-row">
          <label className="label-one">Enter Full Name</label>
          <input
            id="advisorName"
            name="advisorName"
            type="text"
            className={`form-input ${errors.advisorName ? "has-error" : ""}`}
            value={formData.advisorName}
            onChange={handleChange}
            placeholder="e.g. Avinash Sharma"
            autocomplete="off"
          />
          {errors.advisorName && (
            <div className="error-text">{errors.advisorName}</div>
          )}
        </div>
        <div className="form-row">
          <label className="label-one">Mobile No.</label>
          <div className="flag-input">
            <div className="flag-box">
              <img className="country-flag" src={IndiaFlag} alt="India" />
              <span className="dial-code">+91</span>
            </div>
            <input
              id="advisorMobileNumber"
              name="advisorMobileNumber"
              pattern="[0-9]*"
              inputMode="numeric"
              type="tel"
              maxLength={10}
              className={`form-input ${errors.advisorMobileNumber ? "has-error" : ""}`}
              value={formData.advisorMobileNumber}
              onChange={handleChange}
              placeholder="e.g. 98762 72383"
              autocomplete="off"
            />
          </div>
          {errors.advisorMobileNumber && (
            <div className="error-text">{errors.advisorMobileNumber}</div>
          )}
        </div>

        <div className="form-row">
          <label className="label-one">Email ID</label>
          <input
            id="advisorEmailID"
            name="advisorEmailID"
            type="email"
            className={`form-input ${errors.advisorEmailID ? "has-error" : ""}`}
            value={formData.advisorEmailID}
            onChange={handleChange}
            placeholder="Deepak@indusindnipponlife.com"
            autocomplete="off"
          />
          {errors.advisorEmailID && (
            <div className="error-text">{errors.advisorEmailID}</div>
          )}
        </div>

        <div className="form-row">
          <label className="label-one">Advisor Code</label>
          <input
            id="advisorCode"
            name="advisorCode"
            type="text"
            className={`form-input ${errors.advisorCode ? "has-error" : ""}`}
            value={formData.advisorCode}
            onChange={handleChange}
            placeholder="e.g. XYZ"
            autocomplete="off"
          />
          {errors.advisorCode && (
            <div className="error-text">{errors.advisorCode}</div>
          )}
        </div>

        <div className="seprater w-full"></div>

        <div className="form-row w-full">
          <label className="label-one label-top">
            Write Your Concerns{" "}
            <span className=" file-size-text  ">(upto 150 characters)</span>
          </label>
          <textarea
            id="concerns"
            name="concerns"
            type="number"
            className={`form-input ${errors.concerns ? "has-error" : ""}`}
            value={formData.concerns}
            onChange={handleChange}
            placeholder="write here in brief"
            autocomplete="off"
            maxLength={150}
          ></textarea>
          {errors.concerns && (
            <div className="error-text">{errors.concerns}</div>
          )}
          <p className="mt-1 text-caption regular text-arear">
            I may receive offers from Reliance General Insurance and Reliance
            Capital group companies upon responding to their advertisements.
          </p>
        </div>

        <div className="form-actions">
          <button
            className="submit-button btn primary-button regular "
            type="submit"
            disabled={!isFormValid()}
            onClick={handleSubmit}
          >
            {submitting ? UI_LABELS.SUBMITTING : UI_LABELS.SUBMIT}
          </button>
        </div>
      </div>

      {/* SUCCESS MODAL */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setFormData(initialFormState);
          setApiErrorMsg("");
          setErrors("");
        }}
        title="You’re all set!"
        type="success"
        btnTitle="Done"
      >
        <p>{SUCCESS_MESSAGES.FORM_SUBMITTED}</p>
      </Modal>
      {/* ERROR MODAL */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setFormData(initialFormState);
          setApiErrorMsg("");
          setErrors("");
        }}
        title="Something went wrong!"
        type="error"
        btnTitle="Try Again"
      >
        <p>{apiErrorMsg}</p>
      </Modal>
    </div>
  );
};

export default AdvisorForm;
